// raumWahl.ts — Auswahl-Logik der Ort-/Raum-Zeile im Termin-Dialog (PO 27.07.2026).
//
// Bewusst ohne React/DOM, damit die Regeln per Vitest prüfbar sind: welcher Raum
// bei welcher Eingabe erscheint, in welcher Reihenfolge, und was ein Klick mit
// den beiden Graph-Feldern macht.
//
// Der Hintergrund für die Zweiteilung: ein gewählter Raum geht als ATTENDEE vom
// Typ `resource` an Graph (`roomAddress`), ein freier Ort dagegen als Feld
// `location` (siehe buildAttendeePayload/buildCreateEventInput in
// terminFormShared.ts). Für den Anwender ist das EIN Feld — die Übersetzung in
// beide Werte passiert hier.

import type { RaumVerfuegbarkeit, RoomDto } from "../../services/calendarService";
import type { RoomAvailability } from "./terminFormShared";

/** Die beiden Graph-Werte hinter der einen Ort-Zeile. */
export interface OrtWahl {
    /** Graph-Feld `location` — der angezeigte Ort. */
    location: string;
    /** Raum-Mailadresse; leer, wenn es kein buchbarer Raum ist. */
    roomAddress: string;
}

/**
 * Raum gewählt: seine Adresse wird gebucht, sein Name steht als Ort im Termin
 * (so macht es Outlook — im Kalender liest man den Raumnamen, nicht die
 * Mailadresse). Ohne `displayName` fällt der Ort auf die Adresse zurück, sonst
 * stünde dort nichts.
 */
export function ortAusRaum(r: RoomDto): OrtWahl {
    return { location: r.displayName || r.emailAddress, roomAddress: r.emailAddress };
}

/**
 * Freitext: nur ein Ort, kein gebuchter Raum. Wichtig ist das Leeren von
 * `roomAddress` — sonst bliebe ein zuvor gewählter Raum als Ressource am Termin
 * hängen, obwohl im Feld längst eine Adresse steht.
 */
export function ortAusFreitext(text: string): OrtWahl {
    return { location: text, roomAddress: "" };
}

const norm = (s: string) =>
    s
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "");

/**
 * Räume zur Eingabe. Leere Eingabe ⇒ ALLE Räume — das ist die Anforderung
 * „auch bei leerem Feld durchsuchbar": ein Klick ins Feld zeigt die vollständige
 * Liste, wie es die frühere Chip-Liste tat.
 *
 * Gesucht wird über Name, Adresse, Gebäude und Etage: Räume heißen intern oft
 * nur „Besprechung 2", auffindbar sind sie über das Gebäude.
 */
export function filterRaeume(rooms: readonly RoomDto[], query: string): RoomDto[] {
    const q = norm(query.trim());
    if (!q) return [...rooms];
    return rooms.filter((r) =>
        [r.displayName, r.emailAddress, r.building, r.floorLabel]
            .filter(Boolean)
            .some((f) => norm(String(f)).includes(q)),
    );
}

/**
 * Freie Räume nach oben, dann unbekannte, dann belegte — innerhalb einer Gruppe
 * bleibt die Eingangsreihenfolge (stabil, kein Springen beim Nachladen der
 * Verfügbarkeiten).
 *
 * Belegte Räume bleiben in der Liste: wer weiß, dass ein Raum gleich frei wird,
 * soll ihn trotzdem wählen können. Sie sind nur als „Belegt" markiert.
 */
const AVAIL_RANK: Record<RoomAvailability, number> = { free: 0, unknown: 1, busy: 2 };

export function sortiereRaeume(
    rooms: readonly RoomDto[],
    statusByRoom: Record<string, RoomAvailability>,
): RoomDto[] {
    return rooms
        .map((r, i) => ({ r, i, st: raumStatus(r, statusByRoom) }))
        .sort((a, b) => AVAIL_RANK[a.st] - AVAIL_RANK[b.st] || a.i - b.i)
        .map((x) => x.r);
}

/**
 * Verfügbarkeit eines Raums. Die Status-Map ist auf KLEINGESCHRIEBENE Adressen
 * gekeyt (so verdichtet `roomAvailabilityFromResult` das Graph-Ergebnis) —
 * ein Lookup mit der Originaladresse liefert sonst still „unknown".
 */
export function raumStatus(
    r: RoomDto,
    statusByRoom: Record<string, RoomAvailability>,
): RoomAvailability {
    return statusByRoom[r.emailAddress.toLowerCase()] ?? "unknown";
}

// ── Verfügbarkeit in der Raumwahl des Termin-Formulars ──────────────────────

const uhrzeit = (iso: string): string =>
    new Date(iso).toLocaleTimeString("de-DE", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "Europe/Berlin",
    });

const datumKurz = (iso: string): string =>
    new Date(iso).toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        timeZone: "Europe/Berlin",
    });

/** Belegte Blöcke als Text: „09:00–10:30, 14:00–15:00“; über Tage hinweg mit Datum. */
export function belegungText(belegt: readonly { start: string; end: string }[]): string {
    return belegt
        .map((b) => {
            const gleicherTag = datumKurz(b.start) === datumKurz(b.end);
            return gleicherTag
                ? `${uhrzeit(b.start)}–${uhrzeit(b.end)}`
                : `${datumKurz(b.start)} ${uhrzeit(b.start)}–${datumKurz(b.end)} ${uhrzeit(b.end)}`;
        })
        .join(", ");
}

/** Beschriftung einer Raum-Option: „Lounge — frei“ / „Lounge — belegt (09:00–10:30)“. */
export function raumOptionText(r: RoomDto, v: RaumVerfuegbarkeit | undefined): string {
    const name = r.displayName || r.emailAddress;
    if (!v || v.status === "unbekannt") return name;
    if (v.status === "frei") return `${name} — frei`;
    return `${name} — belegt (${belegungText(v.belegt)})`;
}

/**
 * Belegte Räume sind nicht wählbar: Exchange-Raumpostfächer lehnen
 * überschneidende Buchungen standardmäßig ab. Unbekannt bleibt wählbar —
 * ein Graph-Aussetzer soll die Raumwahl nicht sperren.
 */
export function raumWaehlbar(v: RaumVerfuegbarkeit | undefined): boolean {
    return v?.status !== "belegt";
}

/** Verfügbarkeit je Raum (Schlüssel: kleingeschriebene Adresse). */
export function verfuegbarkeitNachAdresse(
    liste: readonly RaumVerfuegbarkeit[],
): Record<string, RaumVerfuegbarkeit> {
    const out: Record<string, RaumVerfuegbarkeit> = {};
    for (const v of liste) out[v.emailAddress.toLowerCase()] = v;
    return out;
}
